import { useEffect, useId, useState } from "react";
import type { FC } from "react";

import { useGetHistoryAssignmentQuery } from "api/generated";
import { processApiError } from "utils/processApiError";
import HistoryIcon from "images/icons/15-historie.svg";
import StrawberryIcon from "images/icons/20-strawberry.svg";

import { EnvelopeIcon, Lightbox, Loading } from "components";
import ArrowIcon from "images/icons/10-arrow2.svg";
import { EnvelopeDot, type TEnvelopeDotColor } from "components/Envelope/EnvelopeDot";

interface Props {
  userAssignmentId: string;
  assignmentId: string;
  onHide: () => void;
}

interface Row {
  dateTested: string;
  envelopeBefore: TEnvelopeDotColor | null;
  envelopeNow: TEnvelopeDotColor | null;
}

// AIDEV-NOTE: API types envelopes as plain String; todas-app writes only
// UserAssignment.Envelope values, so anything else is treated as "no envelope".
const ENVELOPE_DOT_COLORS = ["red", "green", "yellow"] as const;

const toEnvelopeColor = (value?: string | null): TEnvelopeDotColor | null =>
  ENVELOPE_DOT_COLORS.find(color => color === value) ?? null;

export const AssignmentHistoryModal: FC<Props> = ({ userAssignmentId, assignmentId, onHide }) => {
  const { data, isLoading, error } = useGetHistoryAssignmentQuery({ assignmentId });
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    if (error === undefined) return;

    processApiError(error);
  }, [error]);

  useEffect(() => {
    if (!data) return;

    const parsedData: Row[] = [];
    for (const item of data.userTutoringsAndSessions) {
      const movement = (
        item.session?.envelopeMovements.filter(movement => movement.userAssigment === userAssignmentId) ?? []
      ).at(0);

      if (!movement?.envelopeNow) continue;

      parsedData.push({
        dateTested: item.dateTested,
        envelopeBefore: toEnvelopeColor(movement.envelopeBefore),
        envelopeNow: toEnvelopeColor(movement.envelopeNow),
      });
    }

    setRows(parsedData);
  }, [data]);

  const titleId = useId();

  return (
    // Nadpis nese ikony uvnitř textu a vlastní layout, takže zůstává v těle a dialog
    // se jím jen pojmenuje. [TD-344]
    <Lightbox show onHide={onHide} className="modal--400" bodyClassName={"pt-0 px-32px pb-40px"} bodyTitleId={titleId}>
      <h2 id={titleId} className="d-flex align-items-center gap-3 pb-3 fs-16">
        <HistoryIcon />
        <span className="lh-100">
          Historie úlohy v <StrawberryIcon /> tréninku
        </span>
      </h2>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {rows.length ? (
            <div>
              {rows.map(row => (
                <div key={row.dateTested} className="assignment-history__row">
                  <EnvelopeDot type={row.envelopeNow ?? row.envelopeBefore} />
                  <div className="flex-grow-1">{row.dateTested}</div>

                  {row.envelopeBefore ? (
                    <div className="assignment-history__movements fs-13">
                      <EnvelopeIcon type={row.envelopeBefore} />
                      <ArrowIcon className="assignment-history__arrow" />
                      <EnvelopeIcon type={row.envelopeNow ?? row.envelopeBefore} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div>Bez historie procvičování</div>
          )}
        </>
      )}
    </Lightbox>
  );
};
